import BaseRepository from './BaseRepository';

export interface DraftRecord {
  tenant: string;
  kind: string;
  key: string;
  owner?: string;
  updatedBy?: string;
  updatedMs: number;
  deleted?: boolean;
  data?: Record<string, any>;
}

export interface CreateDraftInput {
  tenant: string;
  kind: string;
  key: string;
  owner?: string;
  data: Record<string, any>;
}

export interface DraftVersion {
  version: number;
  updatedBy: string;
  updatedMs: number;
  draft: any; // JSON value
}

export interface WorkflowHistoryEntry {
  ts: number;
  user: string;
  diffs: any[];
  draft: any;
  version?: number;
}

export interface WorkflowHistoryEntryInput {
  ts: number;
  diffs: any[];
  draft: any;
}

export class WorkflowDraftRepository extends BaseRepository<DraftRecord> {
  async listDrafts(tenant: string, kind: string): Promise<DraftRecord[]> {
    await this.validateRepositorySafety({
      operation: 'LIST_DRAFTS',
      domain: 'WORKFLOW',
      tenantId: tenant,
      entityType: kind,
      criticalityLevel: 'MEDIUM',
      metadata: { kind }
    });

    const query = `
      query ListDrafts($tenant: String!, $kind: String!) {
        listDrafts(input: { tenant: $tenant, kind: $kind }) {
          tenant
          kind
          key
          owner
          updatedBy
          updatedMs
          deleted
          data
        }
      }
    `;

    const result = await this.graphQLClient.query({
      query: this.gql(query),
      variables: { tenant, kind },
      context: {
        headers: { 'x-tenant': tenant }
      }
    });

    const drafts = result.data?.listDrafts ?? [];

    if (this.auditTrail) {
      await this.auditTrail.logOperationSuccess({
        operation: 'LIST_DRAFTS',
        tenantId: tenant,
        entityType: kind,
        entityId: tenant,
        metadata: { count: drafts.length },
        timestamp: Date.now()
      });
    }

    return drafts;
  }

  async createDraft(input: CreateDraftInput): Promise<any> {
    await this.validateRepositorySafety({
      operation: 'CREATE_DRAFT',
      domain: 'WORKFLOW',
      tenantId: input.tenant,
      entityType: input.kind,
      criticalityLevel: 'HIGH',
      metadata: { key: input.key }
    });

    const mutation = `
      mutation CreateDraft($input: CreateDraftInput!) {
        createDraft(input: $input)
      }
    `;

    const result = await this.graphQLClient.mutate({
      mutation: this.gql(mutation),
      variables: { input },
      context: {
        headers: { 'x-tenant': input.tenant }
      }
    });

    if (this.auditTrail) {
      await this.auditTrail.logOperationSuccess({
        operation: 'CREATE_DRAFT',
        tenantId: input.tenant,
        entityType: input.kind,
        entityId: input.key,
        metadata: { created: true },
        timestamp: Date.now()
      });
    }

    return result.data?.createDraft ?? null;
  }

  async listWorkflowDefinitions(tenant: string): Promise<DraftRecord[]> {
    return this.listDrafts(tenant, 'WorkflowDefinition');
  }

  async listWorkflowInstances(tenant: string): Promise<DraftRecord[]> {
    return this.listDrafts(tenant, 'Instance');
  }

  async createWorkflowInstanceDraft(
    input: Omit<CreateDraftInput, 'kind'>
  ): Promise<any> {
    return this.createDraft({ ...input, kind: 'Instance' });
  }

  async createWorkflowDefinitionDraft(
    input: Omit<CreateDraftInput, 'kind'>
  ): Promise<any> {
    return this.createDraft({ ...input, kind: 'WorkflowDefinition' });
  }

  /**
   * Delete a draft by tenant, kind, and key
   * Repository pattern compliant with audit trail
   */
  async deleteDraft(tenant: string, kind: string, key: string): Promise<boolean> {
    await this.validateRepositorySafety({
      operation: 'DELETE_DRAFT',
      domain: 'WORKFLOW',
      tenantId: tenant,
      entityType: kind,
      criticalityLevel: 'MEDIUM',
      metadata: { key }
    });

    const mutation = `
      mutation DeleteDraft($input: DeleteDraftInput!) {
        deleteDraft(input: $input)
      }
    `;

    const result = await this.graphQLClient.mutate({
      mutation: this.gql(mutation),
      variables: {
        input: {
          tenant,
          kind,
          key
        }
      },
      context: {
        headers: { 'x-tenant': tenant }
      }
    });

    const deleted = result.data?.deleteDraft ?? false;

    if (this.auditTrail && deleted) {
      await this.auditTrail.logOperationSuccess({
        operation: 'DELETE_DRAFT',
        tenantId: tenant,
        entityType: kind,
        entityId: key,
        metadata: { deleted: true },
        timestamp: Date.now()
      });
    }

    return deleted;
  }

  /**
   * Get version history for a draft
   * Repository pattern compliant with audit trail
   */
  async getDraftVersions(
    tenant: string,
    kind: string,
    key: string,
    limit?: number
  ): Promise<DraftVersion[]> {
    await this.validateRepositorySafety({
      operation: 'GET_DRAFT_VERSIONS',
      domain: 'WORKFLOW',
      tenantId: tenant,
      entityType: kind,
      criticalityLevel: 'LOW',
      metadata: { key, limit }
    });

    const query = `
      query GetDraftVersions($tenant: String!, $kind: String!, $key: String!, $limit: Int) {
        getDraftVersions(tenant: $tenant, kind: $kind, key: $key, limit: $limit) {
          version
          updatedBy
          updatedMs
          draft
        }
      }
    `;

    const result = await this.graphQLClient.query({
      query: this.gql(query),
      variables: { tenant, kind, key, limit },
      context: {
        headers: { 'x-tenant': tenant }
      }
    });

    const versions = result.data?.getDraftVersions ?? [];

    if (this.auditTrail) {
      await this.auditTrail.logOperationSuccess({
        operation: 'GET_DRAFT_VERSIONS',
        tenantId: tenant,
        entityType: kind,
        entityId: key,
        metadata: { count: versions.length, limit },
        timestamp: Date.now()
      });
    }

    return versions;
  }

  /**
   * Get a single draft with full data
   * Repository pattern compliant with audit trail
   */
  async getDraft(tenant: string, kind: string, key: string): Promise<any> {
    await this.validateRepositorySafety({
      operation: 'GET_DRAFT',
      domain: 'WORKFLOW',
      tenantId: tenant,
      entityType: kind,
      criticalityLevel: 'LOW',
      metadata: { key }
    });

    const query = `
      query GetDraft($input: GetDraftInput!) {
        getDraft(input: $input) {
          id
          tenant
          kind
          key
          draft
        }
      }
    `;

    const result = await this.graphQLClient.query({
      query: this.gql(query),
      variables: { input: { tenant, kind, key } },
      context: {
        headers: { 'x-tenant': tenant }
      }
    });

    const draft = result.data?.getDraft ?? null;

    if (this.auditTrail && draft) {
      await this.auditTrail.logOperationSuccess({
        operation: 'GET_DRAFT',
        tenantId: tenant,
        entityType: kind,
        entityId: key,
        metadata: { hasDraft: !!draft },
        timestamp: Date.now()
      });
    }

    return draft;
  }

  /**
   * Update a draft
   * Repository pattern compliant with audit trail
   */
  async updateDraft(
    tenant: string,
    kind: string,
    key: string,
    data: Record<string, any>
  ): Promise<boolean> {
    await this.validateRepositorySafety({
      operation: 'UPDATE_DRAFT',
      domain: 'WORKFLOW',
      tenantId: tenant,
      entityType: kind,
      criticalityLevel: 'MEDIUM',
      metadata: { key, dataKeys: Object.keys(data) }
    });

    const mutation = `
      mutation SaveDraft($input: SaveDraftInput!) {
        saveDraft(input: $input) {
          ok
          key
        }
      }
    `;

    const result = await this.graphQLClient.mutate({
      mutation: this.gql(mutation),
      variables: {
        input: {
          tenant,
          kind,
          key,
          draft: data
        }
      },
      context: {
        headers: { 'x-tenant': tenant }
      }
    });

    const success = result.data?.saveDraft?.ok ?? false;

    if (this.auditTrail && success) {
      await this.auditTrail.logOperationSuccess({
        operation: 'UPDATE_DRAFT',
        tenantId: tenant,
        entityType: kind,
        entityId: key,
        metadata: { updated: true },
        timestamp: Date.now()
      });
    }

    return success;
  }

  /**
   * List available workflow kinds (types)
   * Repository pattern compliant with audit trail
   */
  async listKinds(tenant: string): Promise<string[]> {
    await this.validateRepositorySafety({
      operation: 'LIST_KINDS',
      domain: 'WORKFLOW',
      tenantId: tenant,
      entityType: 'Kind',
      criticalityLevel: 'LOW',
      metadata: {}
    });

    const query = `
      query FormsKinds {
        listKinds
      }
    `;

    const result = await this.graphQLClient.query({
      query: this.gql(query),
      variables: {},
      context: {
        headers: { 'x-tenant': tenant }
      }
    });

    const kinds = (result.data?.listKinds as string[]) || [];

    if (this.auditTrail) {
      await this.auditTrail.logOperationSuccess({
        operation: 'LIST_KINDS',
        tenantId: tenant,
        entityType: 'Kind',
        entityId: 'all',
        metadata: { count: kinds.length },
        timestamp: Date.now()
      });
    }

    return kinds;
  }

  /**
   * List dictionary values for a specific kind and property
   * Used for filter value suggestions
   * Repository pattern compliant with audit trail
   */
  async listDictionary(tenant: string, kind: string): Promise<string[]> {
    await this.validateRepositorySafety({
      operation: 'LIST_DICTIONARY',
      domain: 'WORKFLOW',
      tenantId: tenant,
      entityType: kind,
      criticalityLevel: 'LOW',
      metadata: { kind }
    });

    const query = `
      query FormsListDictionary($k: String!) {
        listDictionary(kind: $k)
      }
    `;

    const result = await this.graphQLClient.query({
      query: this.gql(query),
      variables: { k: kind },
      context: {
        headers: { 'x-tenant': tenant }
      }
    });

    const dictionary = (result.data?.listDictionary as string[]) || [];

    if (this.auditTrail) {
      await this.auditTrail.logOperationSuccess({
        operation: 'LIST_DICTIONARY',
        tenantId: tenant,
        entityType: kind,
        entityId: kind,
        metadata: { count: dictionary.length },
        timestamp: Date.now()
      });
    }

    return dictionary;
  }

  /**
   * Get workflow change history
   * Repository pattern compliant with audit trail
   */
  async getWorkflowHistory(
    tenant: string,
    key: string,
    limit?: number
  ): Promise<WorkflowHistoryEntry[]> {
    await this.validateRepositorySafety({
      operation: 'GET_WORKFLOW_HISTORY',
      domain: 'WORKFLOW',
      tenantId: tenant,
      entityType: 'WorkflowHistory',
      criticalityLevel: 'LOW',
      metadata: { key, limit }
    });

    const query = `
      query WorkflowHistory($tenant: String!, $key: String!, $limit: Int) {
        workflowHistory(tenant: $tenant, key: $key, limit: $limit) {
          ts
          user
          diffs
          draft
          version
        }
      }
    `;

    const result = await this.graphQLClient.query({
      query: this.gql(query),
      variables: { tenant, key, limit: limit || 50 },
      context: {
        headers: { 'x-tenant': tenant }
      }
    });

    const history = (result.data?.workflowHistory as WorkflowHistoryEntry[]) || [];

    if (this.auditTrail) {
      await this.auditTrail.logOperationSuccess({
        operation: 'GET_WORKFLOW_HISTORY',
        tenantId: tenant,
        entityType: 'WorkflowHistory',
        entityId: key,
        metadata: { count: history.length, limit },
        timestamp: Date.now()
      });
    }

    return history;
  }

  /**
   * Upsert workflow change history entry
   * Repository pattern compliant with audit trail
   */
  async upsertWorkflowHistory(
    tenant: string,
    key: string,
    entry: WorkflowHistoryEntryInput
  ): Promise<{ success: boolean; version?: number }> {
    await this.validateRepositorySafety({
      operation: 'UPSERT_WORKFLOW_HISTORY',
      domain: 'WORKFLOW',
      tenantId: tenant,
      entityType: 'WorkflowHistory',
      criticalityLevel: 'MEDIUM',
      metadata: { key, hasDiffs: !!entry.diffs }
    });

    const mutation = `
      mutation UpsertWorkflowHistory($input: WorkflowHistoryEntryInput!) {
        upsertWorkflowHistory(input: $input) {
          success
          version
        }
      }
    `;

    const result = await this.graphQLClient.mutate({
      mutation: this.gql(mutation),
      variables: {
        input: {
          tenant,
          key,
          entry
        }
      },
      context: {
        headers: { 'x-tenant': tenant }
      }
    });

    const response = result.data?.upsertWorkflowHistory ?? { success: false };

    if (this.auditTrail && response.success) {
      await this.auditTrail.logOperationSuccess({
        operation: 'UPSERT_WORKFLOW_HISTORY',
        tenantId: tenant,
        entityType: 'WorkflowHistory',
        entityId: key,
        metadata: { version: response.version },
        timestamp: Date.now()
      });
    }

    return response;
  }
}

export default WorkflowDraftRepository;
